// Shared types for the Agent Hotline chat. The in-page widget and the popped-out
// window render from these same shapes so the two views cannot drift.

export type ChatType = 'agent' | 'commercial' | 'carrier' | 'it';

export const CHAT_TITLES: Record<ChatType, string> = {
  agent: 'Agent Hotline',
  commercial: 'Commercial Hotline',
  carrier: 'Carrier Relations',
  it: 'IT Hotline',
};

export type ChatMessageKind = 'agent' | 'customer' | 'system';

export interface ChatMessage {
  // Stable key for React. Connect supplies an item id; locally-generated system
  // and optimistic messages get a synthetic one.
  id: string;
  kind: ChatMessageKind;
  sender: string;
  content: string;
  // ISO 8601 instant the message was sent. Taken from the Connect transcript
  // item's AbsoluteTime rather than arrival time, so a delayed delivery still
  // reports when it was actually sent — agents read these to judge how long a
  // request took (WIPE-981).
  at: string;
  // Set on the local echo of an outbound message, until Connect delivers the
  // server's copy back and it is reconciled away (see mergeMessage).
  pending?: boolean;
}

// Connection lifecycle only. Deliberately says nothing about where the
// conversation is rendered: an ended chat still has a transcript to show, and it
// has to show it in exactly one place (see ChatSurface).
export type ChatStatus = 'idle' | 'starting' | 'open' | 'ended' | 'error';

// Which surface is rendering the conversation, if any. At most one does at a time
// — the session lives in this tab regardless, and only the view moves. Kept
// separate from ChatStatus because the two change independently: folding "popped
// out" into the status meant ending a popped-out chat flipped the status to
// 'ended' and silently brought the in-page panel back, so the transcript rendered
// in both the popup and the page at once.
//
// 'hidden' is a minimized live chat: the contact stays connected with nothing
// drawing it, and the launch button becomes "Return to Chat" so there is always a
// way back in.
export type ChatSurface = 'panel' | 'popup' | 'hidden';

// Messages relayed between the parent tab (which owns the Connect session) and
// the popped-out window (which is only a view). The parent is authoritative:
// the popup sends intents and renders what it is told.
export type RelayMessage =
  | { type: 'popup-ready' }
  | { type: 'popup-closed' }
  | { type: 'history'; messages: ChatMessage[]; title: string; ended: boolean }
  | { type: 'message'; message: ChatMessage }
  | { type: 'ended' }
  | { type: 'send'; content: string }
  | { type: 'end' };
